import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { UserDto } from '@/types/auth';
import type { AssignPickListRequest, PickListDto, RecordPicksRequest } from '@/types/outbound';

export default function PickingPage() {
  const { t } = useTranslation('picking');
  const queryClient = useQueryClient();

  const { data: pickLists = [], isLoading } = useQuery({
    queryKey: ['pick-lists'],
    queryFn: async () => (await apiClient.get<PickListDto[]>('/pick-lists')).data,
  });
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await apiClient.get<UserDto[]>('/users')).data,
  });

  const { data: config } = useProcessConfiguration();
  const barcodeMandatory = config?.barcodeMandatory ?? false;

  const [selected, setSelected] = useState<PickListDto | null>(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [pickedQtyByLine, setPickedQtyByLine] = useState<Record<string, number>>({});
  // Barcode-wise mode only — true once a line's own physical label has been scanned via the shared
  // Scan Barcode field below (see tryMatchScan). Drives both the scanned-rows-to-top ordering and
  // the green "confirmed" highlight, same pattern as production/MaterialIssuePage.tsx.
  const [checkedByLine, setCheckedByLine] = useState<Record<string, boolean>>({});
  // The one shared scan input for barcode mode — mirrors MaterialIssuePage.tsx exactly.
  const [scanInput, setScanInput] = useState('');
  // Scanned line ids, most-recently-scanned first — drives "scanned rows float to the top".
  const [scanOrder, setScanOrder] = useState<string[]>([]);

  const openTask = (pickList: PickListDto) => {
    setSelected(pickList);
    setAssignUserId(pickList.assignedToUserId ?? '');
    setPickedQtyByLine(Object.fromEntries(pickList.lines.map((l) => [l.id, l.pickedQty || l.requestedQty])));
    // Under Barcode Required, every line with a barcode starts unconfirmed (must be scanned) —
    // without it, there's nothing to scan against, so every line is already "ready".
    setCheckedByLine(Object.fromEntries(pickList.lines.map((l) => [l.id, !barcodeMandatory || !l.barcode])));
    setScanInput('');
    setScanOrder([]);
  };

  // Tries to match `value` against a not-yet-checked line's barcode — on every keystroke (a scanner
  // types the full value near-instantly) and explicitly on Enter. On a match: the line is checked,
  // stamped, and floated to the top; the field clears for the next scan. Mirrors
  // production/MaterialIssuePage.tsx's tryMatchScan exactly.
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed || !selected) {
      return false;
    }
    const matchLine = selected.lines.find((l) => !checkedByLine[l.id] && (l.barcode ?? '').toUpperCase() === trimmed);
    if (!matchLine) {
      return false;
    }
    setCheckedByLine((prev) => ({ ...prev, [matchLine.id]: true }));
    setScanOrder((prev) => [matchLine.id, ...prev.filter((id) => id !== matchLine.id)]);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) {
      setScanInput(value);
    }
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  // Scanned lines float to the top (most-recent first); everything else keeps its original order.
  const orderedLines = useMemo(() => {
    if (!selected) {
      return [];
    }
    const scannedFirst = scanOrder.map((id) => selected.lines.find((l) => l.id === id)).filter((l): l is NonNullable<typeof l> => Boolean(l));
    const rest = selected.lines.filter((l) => !scanOrder.includes(l.id));
    return [...scannedFirst, ...rest];
  }, [selected, scanOrder]);

  // Complete & Update Stock is blocked until every barcode-bearing line has been scanned — Save
  // Picks stays available throughout so partial scan progress can still be persisted.
  const allBarcodesReady = useMemo(() => {
    if (!barcodeMandatory || !selected) {
      return true;
    }
    return selected.lines.every((l) => (l.barcode ? checkedByLine[l.id] : true));
  }, [barcodeMandatory, selected, checkedByLine]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pick-lists'] });
    queryClient.invalidateQueries({ queryKey: ['packing-tasks-pending'] });
  };

  const assignMutation = useMutation({
    mutationFn: () => {
      const payload: AssignPickListRequest = { userId: assignUserId };
      return apiClient.post(`/pick-lists/${selected!.id}/assign`, payload);
    },
    onSuccess: () => {
      invalidate();
      setSelected(null);
    },
  });

  const scanMutation = useMutation({
    mutationFn: () => {
      const payload: RecordPicksRequest = { lines: Object.entries(pickedQtyByLine).map(([pickListLineId, pickedQty]) => ({ pickListLineId, pickedQty })) };
      return apiClient.post(`/pick-lists/${selected!.id}/scan`, payload);
    },
    onSuccess: invalidate,
  });

  const completeMutation = useMutation({
    mutationFn: () => apiClient.post(`/pick-lists/${selected!.id}/complete`),
    onSuccess: () => {
      invalidate();
      setSelected(null);
    },
  });

  const columns: DataTableColumn<PickListDto>[] = [
    { field: 'pickListNumber', headerName: t('table.number') },
    { field: 'mrNumber', headerName: t('table.mr') },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'assignedToUserName', headerName: t('table.assignedTo'), render: (r) => r.assignedToUserName ?? '—', filterable: true },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.PICKING_EXECUTE}>
          {r.status !== 'Completed' && (
            <Button size="small" variant="outlined" onClick={() => openTask(r)}>{t('open')}</Button>
          )}
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <DataTable columns={columns} rows={pickLists} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="pick-lists" />

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>{selected.pickListNumber}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <SearchableSelect
                  label={t('dialog.assignTo')} value={assignUserId} onChange={setAssignUserId} fullWidth size="small"
                  options={users.map((u) => ({ value: u.id, label: u.fullName }))}
                />
                <Button size="small" variant="outlined" disabled={!assignUserId || assignMutation.isPending} onClick={() => assignMutation.mutate()} sx={{ alignSelf: 'flex-start' }}>
                  {t('dialog.assign')}
                </Button>

                <Divider />

                {barcodeMandatory && (
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <TextField
                      label={t('dialog.scanBarcode')}
                      value={scanInput}
                      onChange={(e) => handleScanInputChange(e.target.value)}
                      onKeyDown={handleScanKeyDown}
                      helperText={t('dialog.scanBarcodeHelper')}
                      fullWidth
                      autoFocus
                    />
                    {/* Live scanned-vs-total counter, same pattern as MaterialIssuePage's. */}
                    <Chip
                      label={t('dialog.scannedOfTotal', {
                        scanned: selected.lines.filter((l) => l.barcode && checkedByLine[l.id]).length,
                        total: selected.lines.filter((l) => l.barcode).length,
                      })}
                      color={allBarcodesReady ? 'success' : 'warning'}
                      sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                    />
                  </Stack>
                )}

                <Typography variant="subtitle2">{t('dialog.lines')}</Typography>

                {(barcodeMandatory ? orderedLines : selected.lines).map((line) => {
                  const scanned = barcodeMandatory && Boolean(line.barcode) && Boolean(checkedByLine[line.id]);
                  return (
                    <Box
                      key={line.id}
                      sx={{
                        p: barcodeMandatory ? 1.5 : 0,
                        border: barcodeMandatory ? 1 : 0,
                        borderRadius: 1,
                        borderColor: scanned ? 'success.main' : 'divider',
                        bgcolor: scanned ? 'success.50' : undefined,
                      }}
                    >
                      {/* Two separate rows, not one wrapping flex row — the barcode caption's text
                          length varies (and wraps at narrow widths), and a Picked Qty TextField
                          sharing that same flex line let its floating label overlap the caption
                          above it whenever the row wrapped. Info+status stays on its own row up
                          top; Picked Qty gets its own full row below, so there's nothing for its
                          label to collide with regardless of how much the caption text wraps. */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          {barcodeMandatory && line.barcode ? (
                            // Read-only — selection is scan-driven only (see the Scan Barcode field
                            // above); manually ticking a line here is deliberately not possible.
                            <Checkbox size="small" checked={Boolean(checkedByLine[line.id])} disabled sx={{ mt: -0.5, ml: -1 }} />
                          ) : null}
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{line.materialCode} — {line.materialName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('dialog.suggested')}: {line.locationCode ?? '—'} {line.batchNo ? `· ${line.batchNo}` : ''} · {t('dialog.requested')}: {line.requestedQty}
                            </Typography>
                            {barcodeMandatory ? (
                              line.barcode ? (
                                <Typography
                                  variant="caption" display="block"
                                  color={checkedByLine[line.id] ? 'success.main' : 'text.secondary'}
                                >
                                  {t('dialog.expectedBarcode', { barcode: line.barcode })}
                                  {checkedByLine[line.id] ? ` — ${t('dialog.scanned')}` : ''}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="error" display="block">
                                  {t('dialog.noBarcodeBlocked')}
                                </Typography>
                              )
                            ) : (
                              line.barcode && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t('dialog.barcode')}: {line.barcode}
                                </Typography>
                              )
                            )}
                          </Box>
                        </Stack>
                        <StatusChip status={line.status} />
                      </Stack>
                      <TextField
                        type="number" size="small" label={t('dialog.pickedQty')} sx={{ mt: 1.5, minWidth: 140 }}
                        value={pickedQtyByLine[line.id] ?? 0}
                        onChange={(e) => setPickedQtyByLine((prev) => ({ ...prev, [line.id]: Number(e.target.value) }))}
                        slotProps={barcodeMandatory ? { htmlInput: { readOnly: true } } : undefined}
                      />
                      <Divider sx={{ mt: 1.5 }} />
                    </Box>
                  );
                })}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSelected(null)}>{t('cancel')}</Button>
              <Button variant="outlined" disabled={scanMutation.isPending} onClick={() => scanMutation.mutate()}>{t('dialog.savePicks')}</Button>
              <Button variant="contained" disabled={!allBarcodesReady || completeMutation.isPending} onClick={() => completeMutation.mutate()}>{t('dialog.complete')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
